import React from "react";

import { Reset, ScanAlt } from "@carbon/icons-react";

import TagaLogoColor from "@/assets/images/taga-logo-color.svg";
import { Button } from "@/components/ui/button";

import { QRDef } from "../types/auth.types";
import QRLogin from "./QRLogin";

interface LoginFormProps {
  qrValue: QRDef | null;
  countDown: number;
  isLoading: boolean;
  error: string | null;
  onRefreshQR: () => void;
  onRegister: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  qrValue,
  countDown,
  isLoading,
  error,
  onRefreshQR,
  onRegister,
}) => {
  return (
    <div className="z-[2] rounded-xl bg-white shadow-xl px-10 py-16 w-full max-w-[490px] mb-10">
      <div className="flex flex-col items-center">
        <img
          src={TagaLogoColor}
          alt="Logo taga"
          className="mb-4"
          aria-label="TAGA Logo"
        />

        <h1 className="text-secondary-900 text-xl font-bold mb-10">
          Đăng nhập vào tài khoản
        </h1>

        <div className="relative mb-10 p-3 rounded-md overflow-hidden">
          {error ? (
            <div
              className="flex flex-col items-center justify-center h-[220px] w-[220px] bg-red-50 border border-red-200 rounded-lg"
              aria-live="polite"
            >
              <p className="text-red-600 text-sm text-center px-4">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={onRefreshQR}
                className="mt-2"
                aria-label="Thử lại tạo mã QR"
              >
                Thử lại
              </Button>
            </div>
          ) : (
            <>
              <QRLogin
                value={JSON.stringify(qrValue)}
                isLoading={isLoading}
              />
              {countDown === 0 && !isLoading && (
                <div className="absolute left-0 top-0 right-0 bottom-0 bg-secondary-900/70 rounded-md">
                  <button
                    className="flex flex-col items-center justify-center h-full gap-2 cursor-pointer w-full hover:bg-secondary-900/80 transition-colors"
                    onClick={onRefreshQR}
                    aria-label="Làm mới Qr code"
                    tabIndex={0}
                  >
                    <Reset
                      className="text-white font-semibold"
                      size={40}
                      aria-hidden="true"
                    />
                    <span className="text-white font-semibold">
                      Làm mới Qr code
                    </span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="text-xs text-secondary-600 mb-2 w-full md:flex items-center justify-center gap-1">
          <span>Truy cập ứng dụng TAGA trên di động chọn</span>{" "}
          <div className="inline-flex gap-1 items-center">
            <ScanAlt aria-hidden="true" />
            <span className="font-medium">Quét mã QR</span>
          </div>
        </div>

        <button
          className="text-primary font-medium text-xs hover:underline focus:underline focus:outline-none"
          tabIndex={0}
          aria-label="Làm cách nào để quét mã QR?"
        >
          Làm cách nào để quét mã QR?
        </button>

        <div className="mt-10 w-full flex items-center justify-center relative after:absolute after:contents-[''] after:top-[50%] after:left-0 after:translate-y-[-50%] after:z-[1] after:w-full after:h-[1px] after:bg-secondary-200">
          <span className="text-secondary-400 font-medium text-xs relative z-[2] bg-white px-4">
            Hoặc
          </span>
        </div>

        <div className="text-center mt-10">
          <Button
            variant="secondary"
            className="px-[40px]"
            onClick={onRegister}
            aria-label="Đăng ký tài khoản"
          >
            Đăng ký tài khoản
          </Button>
        </div>
      </div>
    </div>
  );
};
